import React from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Option,
  Tooltip,
} from "@mui/joy";
import EquipmentsSortControls from "./EquipmentsSortControls";
import type { EquipmentsSortControlsProps } from "./EquipmentsSortControls";
import DarkSelect from "../../common/DarkSelect";
import { SCreate, SDownload, TuneIcon } from "../../../styles/StyledMUI";
import type { EquipmentModel, EquipmentPrefilter } from "../../../api/equipment/Equipment.typed";
import IconButtonCTA, { type IconButtonCTAProps } from "../../common/IconButtonCTA";
import { colorTokens, trainingRowBadgeSx } from "../../../styles/globalStyles";
import type { EquipmentExportFormat } from "../hooks/useEquipmentExports";

const NO_PREFILTER_VALUE = "__all__";

const exportMenuSx = {
  zIndex: 10000,
  backgroundColor: `${colorTokens.bg.surfaceMuted} !important`,
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "10px",
  minWidth: 240,
  py: 0.5,
  "& .MuiMenuItem-root": {
    color: `${colorTokens.text.primary} !important`,
    fontSize: { xs: "0.8rem", sm: "0.875rem" },
    minHeight: { xs: 32, sm: 36 },
    "&:hover": {
      backgroundColor: `${colorTokens.bg.hover} !important`,
    },
  },
};

export interface EquipmentsToolbarProps extends EquipmentsSortControlsProps {
  models: EquipmentModel[];
  selectedModelId: number | null;
  canManage: boolean;
  onSelectModel: (_: React.SyntheticEvent | null, value: number | null) => void;
  onCreate: () => void;
  onExportFiltered: (format: EquipmentExportFormat) => void;
  exporting?: boolean;
  activePrefilter?: EquipmentPrefilter | null;
  onApplyPrefilter?: (prefilter: EquipmentPrefilter | null) => void;
  activeCriteriaCount: number;
  filterDrawerOpen: boolean;
  onToggleFilterDrawer: () => void;
}

function EquipmentsToolbarInner({
  models,
  selectedModelId,
  selectedModel,
  canManage,
  onSelectModel,
  onCreate,
  onExportFiltered,
  exporting,
  activePrefilter,
  onApplyPrefilter,
  activeCriteriaCount,
  filterDrawerOpen,
  onToggleFilterDrawer,
  ...sortControlsProps
}: EquipmentsToolbarProps) {
  const canShowCreate = canManage && !!selectedModel;
  const prefilters = React.useMemo(() => selectedModel?.Prefilters || [], [selectedModel]);
  const hasPrefilters = !!onApplyPrefilter && prefilters.length > 0;
  const isFiltered = activeCriteriaCount > 0 || !!activePrefilter;

  const handlePrefilterChange = React.useCallback(
    (_: React.SyntheticEvent | null, value: string | null) => {
      if (!onApplyPrefilter) return;

      if (!value || value === NO_PREFILTER_VALUE) {
        onApplyPrefilter(null);

        return;
      }

      const next = prefilters.find((p) => p.ID === value);
      onApplyPrefilter(next || null);
    },
    [onApplyPrefilter, prefilters],
  );

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 1.5 },
        alignItems: { xs: "stretch", sm: "center" },
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <DarkSelect
        placeholder="Sélectionner un type d'équipement"
        value={selectedModelId}
        onChange={onSelectModel}
        sx={{ minWidth: { xs: "100%", sm: 260 } }}
      >
        {models.length === 0 ? (
          <Option value={-1} disabled>
            Aucun modèle disponible
          </Option>
        ) : (
          models.map((m) => (
            <Option key={m.ID} value={m.ID!}>
              {m.Name}
            </Option>
          ))
        )}
      </DarkSelect>

      {hasPrefilters && (
        <DarkSelect
          placeholder="Filtres rapides"
          value={activePrefilter?.ID ?? NO_PREFILTER_VALUE}
          onChange={handlePrefilterChange}
          sx={{ minWidth: { xs: "100%", sm: 200 } }}
        >
          <Option value={NO_PREFILTER_VALUE}>Tous les équipements</Option>
          {prefilters.map((p) => (
            <Option key={p.ID} value={p.ID}>
              {p.Name}
            </Option>
          ))}
        </DarkSelect>
      )}

      {selectedModel && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            ml: { sm: "auto" },
          }}
        >
          <Tooltip title={filterDrawerOpen ? "Masquer les filtres" : "Afficher les filtres"}>
            <Badge
              badgeContent={activeCriteriaCount}
              color="warning"
              size="sm"
              invisible={activeCriteriaCount === 0}
              sx={trainingRowBadgeSx}
            >
              <IconButtonCTA
                ctaVariant={filterDrawerOpen ? "accent" : "neutral"}
                size="sm"
                aria-label="Basculer le panneau de filtres"
                aria-pressed={filterDrawerOpen}
                onClick={onToggleFilterDrawer}
              >
                <TuneIcon />
              </IconButtonCTA>
            </Badge>
          </Tooltip>

          {canShowCreate && (
            <Tooltip title="Nouvel équipement">
              <IconButtonCTA
                size="sm"
                ctaVariant="accent"
                aria-label="Créer équipement"
                onClick={onCreate}
              >
                <SCreate />
              </IconButtonCTA>
            </Tooltip>
          )}

          <EquipmentsSortControls selectedModel={selectedModel} {...sortControlsProps} />

          <Dropdown>
            <Tooltip title={isFiltered ? "Exporter la liste filtrée" : "Exporter la liste"}>
              <MenuButton
                slots={{ root: IconButtonCTA }}
                slotProps={{
                  root: {
                    ctaVariant: "neutral",
                    size: "sm",
                    "aria-label": isFiltered ? "Exporter la liste filtrée" : "Exporter la liste",
                    disabled: exporting,
                  } satisfies Partial<IconButtonCTAProps>,
                }}
              >
                {exporting ? <CircularProgress size="sm" /> : <SDownload />}
              </MenuButton>
            </Tooltip>
            <Menu placement="bottom-end" sx={exportMenuSx}>
              <MenuItem onClick={() => onExportFiltered("csv")} disabled={exporting}>
                Exporter en CSV{isFiltered ? " (liste filtrée)" : ""}
              </MenuItem>
              <MenuItem onClick={() => onExportFiltered("pdf")} disabled={exporting}>
                Exporter en PDF{isFiltered ? " (liste filtrée)" : ""}
              </MenuItem>
            </Menu>
          </Dropdown>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(EquipmentsToolbarInner);
